import { ContentLoader, NotFoundState, SortArrows } from '@rio-cloud/rio-uikit';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';
import { getSupportSubtopicLabelId, typeSubjectOccorenceIds } from '../constants';
import type { IOccurrence, OccurrenceStatus } from '../types';
import Chip from './Chip';
import { convertMinutesToHours } from '../../../utils/dateUtils';
import { SUPPORT_TABLE_COLUMNS, type SupportTableColumn } from './supportTableColumns';

interface Props {
    data: IOccurrence[];
    isLoading: boolean;
    columnOrder: SupportTableColumn[];
    hiddenColumns: SupportTableColumn[];
    sortBy?: string;
    sortDirection?: string;
    onSort?: (sortBy: string, sortDirection: string) => void;
}

const SORTABLE_COLUMNS = new Set<SupportTableColumn>([
    'tasOccurrenceNumber',
    'supportType',
    'status',
    'chassis',
    'licensePlate',
    'dnDescription',
    'subject',
    'subtopic',
    'responsible',
    'assignedTo',
    'statusUpdatedAt',
    'updatedAt',
]);

const STATUS_LABEL_IDS: Record<OccurrenceStatus, string> = {
    OPEN: 'intl-msg:tas.occurrence.card.open',
    IN_ANALYSIS: 'intl-msg:tas.occurrence.card.inAnalysis',
    ANSWERED: 'intl-msg:tas.occurrence.card.answered',
    FINISHED: 'intl-msg:tas.occurrence.card.finished',
};

const SKELETON_ROW_IDS = Array.from({ length: 8 }, (_, index) => `row-${index}`);

const SupportTable = ({ data, isLoading, columnOrder, hiddenColumns, sortBy, sortDirection, onSort }: Props) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();

    const handleClickItem = (id: number | string) => {
        const params = new URLSearchParams(location.search);
        params.set('id', String(id));

        navigate(`${location.pathname}/details?${params.toString()}`);
    };

    const getSubjectLabel = (subject: string) => {
        const labelId = typeSubjectOccorenceIds[subject?.toUpperCase() as keyof typeof typeSubjectOccorenceIds];

        return labelId ? intl.formatMessage({ id: labelId }) : '-';
    };

    const getSubtopicLabel = (subtopic: string) => {
        const labelId = getSupportSubtopicLabelId(subtopic);
        return labelId ? intl.formatMessage({ id: labelId }) : '-';
    };

    const getTime = (occurrence: IOccurrence) => {
        const time = occurrence.status === 'FINISHED' ? occurrence.timeInTasOccurrence : occurrence.timeInStatus;

        return convertMinutesToHours(time);
    };

    const handleHeaderSort = (column: SupportTableColumn) => {
        if (!SORTABLE_COLUMNS.has(column) || !onSort) return;

        const nextDirection = sortBy === column && sortDirection === 'ASC' ? 'DESC' : 'ASC';
        onSort(column, nextDirection);
    };

    const visibleColumns = columnOrder.filter(column => !hiddenColumns.includes(column));
    const columnLabels = Object.fromEntries(
        SUPPORT_TABLE_COLUMNS.map(column => [column.id, intl.formatMessage({ id: column.labelId })])
    ) as Record<SupportTableColumn, string>;

    const renderCell = (column: SupportTableColumn, occurrence: IOccurrence) => {
        switch (column) {
            case 'tasOccurrenceNumber':
                return <span className='text-medium'>{occurrence.tasOccurrenceNumber || '-'}</span>;
            case 'supportType':
                return <Chip type={occurrence.supportType} />;
            case 'status':
                return intl.formatMessage({ id: STATUS_LABEL_IDS[occurrence.status] });
            case 'licensePlate':
                return occurrence.licensePlate || '-';
            case 'chassis':
                return occurrence.chassis || '-';
            case 'dnDescription':
                return occurrence.dn || '-';
            case 'subject':
                return getSubjectLabel(occurrence.subject);
            case 'subtopic':
                return getSubtopicLabel(occurrence.subtopic);
            case 'responsible':
                return occurrence.responsible || '-';
            case 'assignedTo':
                return occurrence.assignedTo || '-';
            case 'statusUpdatedAt':
                return getTime(occurrence);
            case 'updatedAt':
                return occurrence.updatedAt ? moment(occurrence.updatedAt).format('DD/MM/YYYY HH:mm') : '-';
        }
    };

    if (!isLoading && !data.length) {
        return (
            <div className='margin-top-15 bg-white dark:bg-neutral-800 padding-15'>
                <NotFoundState
                    headline={intl.formatMessage({ id: 'intl-msg:support.listBoard.noOccurrenceHeadline' })}
                    message={intl.formatMessage({ id: 'intl-msg:support.listBoard.noOccurrenceMessage' })}
                    outerClassName='border-none'
                />
            </div>
        );
    }

    return (
        <div className='margin-top-15 bg-white dark:bg-neutral-800 padding-15 overflow-x-auto'>
            <table className='table table-bordered table-head-filled table-hover' style={{ minWidth: 1500 }}>
                <thead>
                    <tr>
                        {visibleColumns.map(column => {
                            const isSortable = SORTABLE_COLUMNS.has(column);

                            return (
                                <th
                                    key={column}
                                    className={isSortable ? 'user-select-none sort-column cursor-pointer' : undefined}
                                    onClick={isSortable ? () => handleHeaderSort(column) : undefined}
                                >
                                    <span>
                                        {isSortable && sortBy === column ? (
                                            <SortArrows direction={sortDirection} />
                                        ) : (
                                            <SortArrows />
                                        )}
                                        <span>{columnLabels[column]}</span>
                                    </span>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {isLoading
                        ? SKELETON_ROW_IDS.map(rowId => (
                            <tr key={rowId}>
                                {visibleColumns.map(column => (
                                    <td key={column}>
                                        <ContentLoader className='height-20 width-100pct' />
                                    </td>
                                ))}
                            </tr>
                        ))
                        : data.map(occurrence => (
                            <tr
                                key={occurrence.id}
                                className='cursor-pointer'
                                tabIndex={0}
                                onClick={() => handleClickItem(occurrence.id)}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        handleClickItem(occurrence.id);
                                    }
                                }}
                            >
                                {visibleColumns.map(column => (
                                    <td key={column}>{renderCell(column, occurrence)}</td>
                                ))}
                            </tr>
                        ))}
                </tbody>
            </table>
        </div>
    );
};

export default SupportTable;
